// Pointer drag rotates the tower (the one core control); keyboard is secondary.
// Buttons are hit-tested against getButtons(phase). touchstart is passive:false.
import { W, H, DRAG_SENS } from './config';
import { Phase, Button } from './types';
import { getButtons } from './ui';

export interface InputHandlers {
  getPhase: () => Phase;
  rotateBy: (dRad: number) => void;
  press: (name: string) => void;
  pauseToggle: () => void;
}

const inside = (b: Button, x: number, y: number) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;

let keyDir = 0;            // -1 / 0 / +1 from held arrow keys
export const keyRotDir = (): number => keyDir;

export function attachInput(canvas: HTMLCanvasElement, h: InputHandlers): void {
  const toLogical = (cx: number, cy: number) => {
    const r = canvas.getBoundingClientRect();
    return { x: (cx - r.left) * (W / r.width), y: (cy - r.top) * (H / r.height) };
  };

  const hitButton = (x: number, y: number): boolean => {
    const btns = getButtons(h.getPhase());
    for (const name in btns) {
      if (inside(btns[name], x, y)) { h.press(name); return true; }
    }
    return false;
  };

  let dragging = false;
  let lastX = 0;

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const { x, y } = toLogical(e.clientX, e.clientY);
    if (hitButton(x, y)) { dragging = false; return; }
    if (h.getPhase() === 'running') {
      dragging = true;
      lastX = x;
      canvas.setPointerCapture(e.pointerId);
    }
  }, { passive: false });

  canvas.addEventListener('pointermove', (e) => {
    if (!dragging || h.getPhase() !== 'running') return;
    const { x } = toLogical(e.clientX, e.clientY);
    h.rotateBy((x - lastX) * DRAG_SENS);   // horizontal drag → rotation; vertical ignored
    lastX = x;
  }, { passive: false });

  const end = () => { dragging = false; };
  canvas.addEventListener('pointerup', (e) => { e.preventDefault(); end(); }, { passive: false });
  canvas.addEventListener('pointercancel', end);

  const setKey = (e: KeyboardEvent, down: boolean) => {
    if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') { e.preventDefault(); keyDir = down ? 1 : 0; }
    else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') { e.preventDefault(); keyDir = down ? -1 : 0; }
  };
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { h.pauseToggle(); return; }
    if ((e.key === ' ' || e.key === 'Enter') && h.getPhase() !== 'running') {
      const btns = getButtons(h.getPhase());
      const first = Object.keys(btns)[0];
      if (first) h.press(first);
      return;
    }
    setKey(e, true);
  });
  window.addEventListener('keyup', (e) => setKey(e, false));
}
